import React from 'react';
import I18n from 'i18n-js';

import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { buttonClassName } from '@reverbdotcom/cadence/components';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import experiments from '@reverbdotcom/commons/src/experiments';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

import { CSP } from '../../../schemas/rql';
import URLHelpers from '../../shared/url_helpers';
import V1CSPPriceGuideModuleLayout from './v1_layout/v1_csp_price_guide_module_layout';
import V2CSPPriceGuideModuleLayoutContainer from './v2_layout/v2_csp_price_guide_module_layout_container';
import ModuleHeaderImage from '../../../../images/price-guide-tool/mobile-header-background.png';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

interface IExternalProps {
  csp: CSP;
}

export function ModuleHeader() {
  return (
    <>
      <div>{
        I18n.t('discovery.priceGuideTool.cspModule.header.title')}
      </div>
      <img src={ModuleHeaderImage} alt={I18n.t('discovery.priceGuideTool.cspModule.header.imageAlt')} />
    </>
  );
}

export default function CSPPriceGuideModule({
  csp,
}: IExternalProps) {
  const user = useUser();
  const isV2LayoutEnabled = useExpEnabled(experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2);

  return (
    <div className="csp-price-guide-module">
      <SiteModule title={<ModuleHeader />}>
        <div className="csp-price-guide-module__content">
          {isV2LayoutEnabled ? (
            <V2CSPPriceGuideModuleLayoutContainer
              cspSlug={csp.slug}
            />
          ) : (
            <V1CSPPriceGuideModuleLayout 
              csp={csp}
            />
          )}
        </div>
      </SiteModule>

      <I18N
        className="csp-price-guide-module__disclaimer"
        tag="p"
        html
        text="discovery.priceGuideTool.cspModule.disclaimer"
        args={{ supOpen: '<sup>', supClose: '</sup>' }}
      />
      <div className="d-flex fx-justify-end mt-2 mobile-mt-1">
        <CoreLink
          className={`${buttonClassName({ variant: 'secondary' })} mr-2 mobile-mr-1 mobile-width-100`}
          to={URLHelpers.priceGuideShowPath(csp.slug)}
          target="_blank"
          rel="nofollow"
          clickEvent={{
            eventName: MParticleEventName.ClickedLinkToPriceGuideToolFromCSPPriceGuideModule,
            componentName: 'CSPPriceGuideModule',
            cspId: csp.id,
            cspSlug: csp.slug,
            entityId: csp.slug,
            entityType: 'url',
            experiments: formatExperimentsForEventAttributes(user, [
              experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
            ]),
          }}
        >
          {I18n.t('discovery.priceGuideTool.cspModule.viewPriceGuideButton')}
        </CoreLink>
        <CoreLink
          className={`${buttonClassName({ variant: 'primary' })} mobile-width-100`}
          clickEvent={{
            eventName: MParticleEventName.ClickedSellYoursOnCsp,
            componentName: 'CSPPriceGuideModule',
            cspId: csp.id,
            cspSlug: csp.slug,
            experiments: formatExperimentsForEventAttributes(user, [
              experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
            ]),
          }}
          to={URLHelpers.prefilledListingFromCSPPath({ cspId: csp.id })}
          target="_blank"
        >
          {I18n.t('discovery.priceGuideTool.show.form.results.sellYoursButtonText')}
        </CoreLink>
      </div>
    </div>
  );
}
